import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/cn';

export interface WizardStep {
  key: string;
  title: string;
  /** Returns true (or a Promise resolving true) when the step's input is valid enough to continue. */
  isValid?: () => boolean;
  content: ReactNode;
}

interface WizardProps {
  steps: WizardStep[];
  onCancel: () => void;
  onFinish: () => void | Promise<void>;
  finishLabel?: string;
  finishing?: boolean;
}

export function Wizard({ steps, onCancel, onFinish, finishLabel = 'Confirm', finishing }: WizardProps) {
  const [index, setIndex] = useState(0);
  const step = steps[index]!;
  const isLast = index === steps.length - 1;
  const canContinue = step.isValid ? step.isValid() : true;

  return (
    <div className="space-y-5">
      <ol className="flex items-center gap-2">
        {steps.map((s, i) => (
          <li key={s.key} className="flex flex-1 items-center gap-2">
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                i < index && 'bg-brand text-white',
                i === index && 'bg-brand-subtle text-brand-hover ring-2 ring-brand',
                i > index && 'bg-surface-sunken text-ink-faint',
              )}
            >
              {i < index ? <Check size={13} /> : i + 1}
            </div>
            {i < steps.length - 1 ? <div className={cn('h-0.5 flex-1', i < index ? 'bg-brand' : 'bg-border')} /> : null}
          </li>
        ))}
      </ol>
      <p className="text-sm font-medium text-ink">{step.title}</p>
      <div>{step.content}</div>
      <div className="flex justify-between gap-2">
        <Button variant="ghost" onClick={index === 0 ? onCancel : () => setIndex((i) => i - 1)}>
          {index === 0 ? 'Cancel' : 'Back'}
        </Button>
        {isLast ? (
          <Button onClick={onFinish} disabled={!canContinue || finishing}>
            {finishLabel}
          </Button>
        ) : (
          <Button onClick={() => setIndex((i) => i + 1)} disabled={!canContinue}>
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
